<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import UniversalTreeSelect from '@/components/createdUi/UniversalTreeSelect.vue'
import LocationTypesManageModal from '@/modules/settings/components/LocationTypesManageModal.vue'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { hospitalStructureApi } from '@/modules/settings/services/hospitalStructureApi'
import { locationTypesApi } from '@/modules/settings/services/locationTypesApi'
import type {
  HospitalLocation,
  HospitalLocationType,
  HospitalLocationWritePayload,
} from '@/modules/settings/types/hospitalStructure.types'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { ITreeSelectStore } from '@/types/ITreeSelectStore'
import {
  findLocationInTree,
  getForbiddenParentIds,
  mapHospitalLocationsToFilterChoices,
} from '@/modules/settings/utils/hospitalStructureTree'

const STRUCTURE_FIELD = 'hospital_structure'
const PARENT_FIELD = 'hospital_parent'
/** Размер страницы корней дерева; бэкенд по умолчанию отдаёт всё (page_size=100000). */
const STRUCTURE_PAGE_SIZE = 25

const { smAndDown } = useDisplay()
const feedbackStore = useFeedbackStore()
const permissionsStore = usePermissionsStore()

const canEditTypes = computed(() => !!permissionsStore.modules.hospital?.location_type_edit)

const tree = ref<HospitalLocation[]>([])
const locationTypes = ref<HospitalLocationType[]>([])
const loading = ref(false)
const detailLoading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const error = ref('')
const selectedId = ref<string | null>(null)
const isCreating = ref(false)
const isTypesModalOpen = ref(false)
const isDeleteConfirmOpen = ref(false)
const showParentSelect = ref(false)
const formRef = ref()
const parentNameFromDetail = ref('')

const form = reactive({
  name: '',
  number: '',
  floor: '',
  description: '',
  location_type: null as string | null,
  parent: null as string | null,
  area: '',
  capacity: '' as string | number | '',
  is_active: true,
  address_city: '',
  address_street: '',
  address_house: '',
})

const rules = {
  name: [(v: string) => !!v?.trim() || 'Наименование обязательно'],
}

const treeChoices = reactive<Record<string, IFilterChoiceItem[]>>({
  [STRUCTURE_FIELD]: [],
  [PARENT_FIELD]: [],
})
const treeSearchChoices = reactive<Record<string, IFilterChoiceItem[]>>({
  [STRUCTURE_FIELD]: [],
  [PARENT_FIELD]: [],
})
const treeCursors = reactive<Record<string, string | null>>({
  [STRUCTURE_FIELD]: null,
  [PARENT_FIELD]: null,
})
const treeLoading = reactive<Record<string, boolean>>({
  [STRUCTURE_FIELD]: false,
  [PARENT_FIELD]: false,
})

function mergeHospitalRoots(existing: HospitalLocation[], incoming: HospitalLocation[]): HospitalLocation[] {
  const seen = new Set(existing.map(node => node.id))
  const merged = [...existing]
  for (const node of incoming) {
    if (!seen.has(node.id)) {
      merged.push(node)
      seen.add(node.id)
    }
  }
  return merged
}

function buildParentChoices(nodes: HospitalLocation[]): IFilterChoiceItem[] {
  const forbidden = getForbiddenParentIds(tree.value, isCreating.value ? null : selectedId.value)
  return mapHospitalLocationsToFilterChoices(nodes, forbidden)
}

function resetParentChoices() {
  treeChoices[PARENT_FIELD] = []
  treeSearchChoices[PARENT_FIELD] = []
  treeCursors[PARENT_FIELD] = null
}

async function fetchTreeChoices(
  fieldKey: string,
  search = '',
  cursor?: string,
): Promise<void> {
  treeLoading[fieldKey] = true
  try {
    const query = search.trim()
    const page = await hospitalStructureApi.listPage({
      search: query || undefined,
      cursor: cursor || undefined,
      page_size: STRUCTURE_PAGE_SIZE,
    })
    const results = page.results || []

    if (fieldKey === STRUCTURE_FIELD) {
      if (query) {
        const mapped = mapHospitalLocationsToFilterChoices(results)
        treeSearchChoices[STRUCTURE_FIELD] = cursor
          ? [...(treeSearchChoices[STRUCTURE_FIELD] || []), ...mapped]
          : mapped
      }
      else {
        tree.value = cursor ? mergeHospitalRoots(tree.value, results) : results
        treeChoices[STRUCTURE_FIELD] = mapHospitalLocationsToFilterChoices(tree.value)
      }
      treeCursors[STRUCTURE_FIELD] = page.next ?? null
      return
    }

    if (fieldKey === PARENT_FIELD) {
      if (!query) {
        // Не затираем уже накопленные корни из STRUCTURE — только дополняем.
        tree.value = mergeHospitalRoots(tree.value, results)
      }

      const mapped = buildParentChoices(results)
      if (query) {
        treeSearchChoices[PARENT_FIELD] = cursor
          ? [...(treeSearchChoices[PARENT_FIELD] || []), ...mapped]
          : mapped
      }
      else {
        treeChoices[PARENT_FIELD] = cursor
          ? [...(treeChoices[PARENT_FIELD] || []), ...mapped]
          : mapped
      }
      treeCursors[PARENT_FIELD] = page.next ?? null
    }
  }
  catch (caught) {
    error.value = getApiError(caught, 'Не удалось загрузить структуру больницы')
  }
  finally {
    treeLoading[fieldKey] = false
  }
}

const hospitalTreeStore: ITreeSelectStore = {
  get treeChoices() {
    return treeChoices
  },
  get treeSearchChoices() {
    return treeSearchChoices
  },
  get treeCursors() {
    return treeCursors
  },
  get treeLoading() {
    return treeLoading
  },
  fetchTreeChoices,
}

const locationTypeItems = computed(() =>
  locationTypes.value.map(item => ({
    title: item.name,
    value: item.id,
  })),
)

const selectedLocationType = computed(() => {
  if (!form.location_type) {
    return null
  }
  return locationTypes.value.find(item => item.id === form.location_type) || null
})

const canHaveAddress = computed(() => {
  if (!form.location_type) {
    return false
  }
  return Boolean(selectedLocationType.value?.can_have_address)
})

const hasSelection = computed(() => isCreating.value || !!selectedId.value)

const formTitle = computed(() => {
  if (isCreating.value) {
    return 'Новое здание / помещение'
  }
  if (selectedId.value) {
    return 'Редактирование'
  }
  return 'Выберите помещение'
})

const parentLabel = computed(() => {
  if (!form.parent) {
    return ''
  }
  const node = findLocationInTree(tree.value, form.parent)
  if (node) {
    return node.number ? `${node.name} (${node.number})` : node.name
  }
  return parentNameFromDetail.value
})

const structureModelValue = computed(() => (isCreating.value ? null : selectedId.value))

watch([selectedId, isCreating], () => {
  resetParentChoices()
})

function emptyForm(parentId: string | null = null) {
  form.name = ''
  form.number = ''
  form.floor = ''
  form.description = ''
  form.location_type = null
  form.parent = parentId
  form.area = ''
  form.capacity = ''
  form.is_active = true
  form.address_city = ''
  form.address_street = ''
  form.address_house = ''
  parentNameFromDetail.value = ''
}

function resolveRelatedId(value: unknown): string | null {
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'object' && value !== null && 'id' in value) {
    const id = (value as { id?: unknown }).id
    return id == null ? null : String(id)
  }
  return String(value)
}

function fillForm(location: HospitalLocation) {
  form.name = location.name || ''
  form.number = location.number || ''
  form.floor = location.floor || ''
  form.description = location.description || ''
  form.location_type = resolveRelatedId(location.location_type)
  form.parent = resolveRelatedId(location.parent)
  form.area = location.area == null ? '' : String(location.area)
  form.capacity = location.capacity == null ? '' : location.capacity
  form.is_active = location.is_active !== false
  form.address_city = location.address?.city || ''
  form.address_street = location.address?.street || ''
  form.address_house = location.address?.house || ''
  parentNameFromDetail.value = location.parent_name || ''

  if (
    location.location_type_detail
    && !locationTypes.value.some(t => t.id === location.location_type_detail!.id)
  ) {
    locationTypes.value.push(location.location_type_detail)
  }
}

function buildPayload(): HospitalLocationWritePayload {
  const areaValue = String(form.area).trim()
  const capacityRaw = String(form.capacity).trim()
  const capacityValue = capacityRaw === '' ? null : Number(capacityRaw)

  const canAddress = canHaveAddress.value
  const addressCity = canAddress ? form.address_city.trim() : ''
  const addressStreet = canAddress ? form.address_street.trim() : ''
  const addressHouse = canAddress ? form.address_house.trim() : ''
  const hasAddress = canAddress && !!(addressCity || addressStreet || addressHouse)

  return {
    name: form.name.trim(),
    number: form.number.trim(),
    floor: form.floor.trim(),
    description: form.description.trim(),
    location_type: form.location_type || null,
    parent: form.parent || null,
    area: areaValue === '' ? null : areaValue,
    capacity: Number.isFinite(capacityValue as number) ? capacityValue : null,
    is_active: form.is_active,
    address: hasAddress
      ? {
          city: addressCity,
          street: addressStreet,
          house: addressHouse,
        }
      : null,
  }
}

async function loadTypes() {
  try {
    locationTypes.value = await locationTypesApi.list()
  }
  catch (caught) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(caught, 'Не удалось загрузить типы помещений'),
    })
  }
}

async function loadTree(_preserveSelection = true) {
  loading.value = true
  error.value = ''
  try {
    tree.value = []
    treeChoices[STRUCTURE_FIELD] = []
    treeSearchChoices[STRUCTURE_FIELD] = []
    treeCursors[STRUCTURE_FIELD] = null
    resetParentChoices()
    await fetchTreeChoices(STRUCTURE_FIELD)
  }
  catch (caught) {
    error.value = getApiError(caught, 'Не удалось загрузить структуру больницы')
  }
  finally {
    loading.value = false
  }
}

async function selectLocation(id: string) {
  if (selectedId.value === id && !isCreating.value) {
    return
  }

  isCreating.value = false
  selectedId.value = id
  detailLoading.value = true
  error.value = ''
  try {
    const detail = await hospitalStructureApi.get(id)
    fillForm(detail)
  }
  catch (caught) {
    error.value = getApiError(caught, 'Не удалось загрузить помещение')
  }
  finally {
    detailLoading.value = false
  }
}

async function onStructureSelect(id: string | number | null) {
  if (id == null || id === '') {
    return
  }
  await selectLocation(String(id))
}

function startCreate() {
  isCreating.value = true
  const parentHint = selectedId.value
  selectedId.value = null
  emptyForm(parentHint)
  formRef.value?.resetValidation?.()
}

function onParentChange(item: IFilterChoiceItem) {
  form.parent = String(item.id)
  parentNameFromDetail.value = item.label
  showParentSelect.value = false
}

function clearParent() {
  form.parent = null
  parentNameFromDetail.value = ''
}

async function save() {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    return
  }

  if (!isCreating.value && selectedId.value && form.parent === selectedId.value) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Объект не может быть родителем самого себя',
    })
    return
  }

  const forbidden = getForbiddenParentIds(tree.value, isCreating.value ? null : selectedId.value)
  if (form.parent && forbidden.has(form.parent)) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Нельзя выбрать дочерний элемент в качестве родителя',
    })
    return
  }

  saving.value = true
  error.value = ''
  try {
    const payload = buildPayload()
    const saved = isCreating.value
      ? await hospitalStructureApi.create(payload)
      : await hospitalStructureApi.update(selectedId.value!, payload)

    feedbackStore.showToast({
      type: 'success',
      message: isCreating.value ? 'Помещение создано' : 'Помещение сохранено',
    })

    await loadTree(false)
    isCreating.value = false
    selectedId.value = saved.id
    fillForm(saved)
  }
  catch (caught) {
    const message = getApiError(caught, 'Не удалось сохранить помещение')
    error.value = message
    feedbackStore.showToast({ type: 'error', message })
  }
  finally {
    saving.value = false
  }
}

function requestDelete() {
  if (!selectedId.value || isCreating.value) {
    return
  }
  isDeleteConfirmOpen.value = true
}

async function handleDeleteConfirm(confirmed: boolean) {
  isDeleteConfirmOpen.value = false
  if (!confirmed || !selectedId.value) {
    return
  }

  deleting.value = true
  error.value = ''
  try {
    await hospitalStructureApi.remove(selectedId.value)
    feedbackStore.showToast({
      type: 'success',
      message: 'Помещение удалено',
    })
    selectedId.value = null
    isCreating.value = false
    emptyForm()
    await loadTree(false)
  }
  catch (caught) {
    const message = getApiError(caught, 'Не удалось удалить помещение')
    error.value = message
    feedbackStore.showToast({ type: 'error', message })
  }
  finally {
    deleting.value = false
  }
}

async function onTypesChanged() {
  await loadTypes()
}

onMounted(async () => {
  await Promise.all([loadTree(false), loadTypes()])
})

defineExpose({
  reload: () => loadTree(true),
})
</script>

<template>
  <div class="pa-6">
    <div
      class="d-flex align-center ga-3 mb-4"
      :class="{ 'flex-column align-stretch': smAndDown }"
    >
      <div class="flex-grow-1" />
      <VBtn
        v-if="canEditTypes"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-price-tag-3-line"
        :block="smAndDown"
        @click="isTypesModalOpen = true"
      >
        Типы помещений
      </VBtn>
      <VBtn
        color="primary"
        prepend-icon="ri-add-line"
        :block="smAndDown"
        @click="startCreate"
      >
        Добавить
      </VBtn>
    </div>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <VRow>
      <VCol cols="12" md="4">
        <div class="text-h6 mb-3">
          Структура
        </div>
        <div class="structure-tree">
          <VProgressLinear
            v-if="loading && !treeChoices[STRUCTURE_FIELD]?.length"
            indeterminate
            color="primary"
          />
          <UniversalTreeSelect
            v-else-if="treeChoices[STRUCTURE_FIELD]?.length || treeLoading[STRUCTURE_FIELD]"
            :model-value="structureModelValue"
            :field-key="STRUCTURE_FIELD"
            :store="hospitalTreeStore"
            placeholder="Поиск по структуре"
            @update:model-value="onStructureSelect"
          />
          <div
            v-else
            class="d-flex flex-column align-center justify-center ga-2 pa-8 text-medium-emphasis border rounded"
          >
            <VIcon icon="ri-building-4-line" size="36" />
            <div class="text-body-2 text-center">
              Помещения ещё не добавлены
            </div>
          </div>
        </div>
      </VCol>

      <VCol cols="12" md="8">
        <div class="text-h6 mb-3">
          {{ formTitle }}
        </div>

        <div
          v-if="!hasSelection"
          class="d-flex flex-column align-center justify-center ga-2 pa-10 text-medium-emphasis border rounded"
        >
          <VIcon icon="ri-cursor-line" size="36" />
          <div class="text-body-2 text-center">
            Выберите помещение в дереве или нажмите «Добавить»
          </div>
        </div>

        <div
          v-else
          class="position-relative"
          :class="{ 'opacity-50': detailLoading }"
        >
          <VProgressLinear
            v-if="detailLoading"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <VForm ref="formRef">
            <VRow>
              <VCol cols="12" md="8">
                <VTextField
                  v-model="form.name"
                  label="Наименование"
                  :rules="rules.name"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.number"
                  label="Номер корпуса / кабинета"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.floor"
                  label="Этаж"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VSelect
                  v-model="form.location_type"
                  :items="locationTypeItems"
                  label="Тип помещения"
                  clearable
                />
              </VCol>
              <VCol cols="12" md="8">
                <VMenu
                  v-model="showParentSelect"
                  :close-on-content-click="false"
                  location="bottom"
                  max-height="480"
                  content-class="entity-form-menu"
                >
                  <template #activator="{ props: menuProps }">
                    <VTextarea
                      v-bind="menuProps"
                      :model-value="parentLabel"
                      variant="outlined"
                      density="comfortable"
                      placeholder="Выберите родительское здание / помещение"
                      persistent-placeholder
                      readonly
                      clearable
                      rows="1"
                      auto-grow
                      no-resize
                      class="invisible-readonly"
                      @click:clear.stop="clearParent"
                    >
                      <template #label>
                        <span>Родительское здание / помещение</span>
                      </template>
                    </VTextarea>
                  </template>

                  <UniversalTreeSelect
                    v-model="form.parent"
                    :field-key="PARENT_FIELD"
                    :store="hospitalTreeStore"
                    placeholder="Поиск по структуре"
                    @change="onParentChange"
                    @close="showParentSelect = false"
                  />
                </VMenu>
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="form.description"
                  label="Описание"
                  rows="2"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.area"
                  label="Площадь (м²)"
                  type="number"
                  min="0"
                  step="0.01"
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="form.capacity"
                  label="Вместимость"
                  type="number"
                  min="0"
                  step="1"
                />
              </VCol>
              <VCol cols="12" md="4" class="d-flex align-center">
                <VSwitch
                  v-model="form.is_active"
                  label="Отображать на портале"
                  color="primary"
                  hide-details
                />
              </VCol>
            </VRow>

            <template v-if="canHaveAddress">
              <VDivider class="my-6" />

              <div class="text-h6 mb-4">
                Адрес расположения
              </div>
              <VRow>
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="form.address_city"
                    label="Город"
                  />
                </VCol>
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="form.address_street"
                    label="Улица"
                  />
                </VCol>
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="form.address_house"
                    label="Дом"
                  />
                </VCol>
              </VRow>
            </template>

            <div
              class="d-flex ga-2 mt-6"
              :class="smAndDown ? 'flex-column' : 'justify-end'"
            >
              <VBtn
                v-if="!isCreating && selectedId"
                variant="outlined"
                color="error"
                prepend-icon="ri-delete-bin-line"
                :loading="deleting"
                :block="smAndDown"
                @click="requestDelete"
              >
                Удалить
              </VBtn>
              <VBtn
                color="primary"
                prepend-icon="ri-save-line"
                :loading="saving"
                :block="smAndDown"
                @click="save"
              >
                Сохранить
              </VBtn>
            </div>
          </VForm>
        </div>
      </VCol>
    </VRow>

    <LocationTypesManageModal
      v-model:is-open="isTypesModalOpen"
      @changed="onTypesChanged"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isDeleteConfirmOpen"
      confirmation-question="Удалить помещение?"
      confirmation-text="Помещение будет скрыто (мягкое удаление)."
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<style scoped lang="scss">
.structure-tree {
  min-height: 360px;
  max-height: min(70vh, 640px);
  display: flex;
  flex-direction: column;

  :deep(.universal-tree-select-container) {
    flex: 1;
    min-height: 0;
    max-height: min(70vh, 640px);
  }

  :deep(.tree-view-content) {
    max-height: none;
  }
}
</style>
